import { Collapse } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { useMap } from "react-map-gl/maplibre";

import { CommentRead, MessagesInner, Point } from "@nens/rana/api-v1";

import { getCompleteName } from "@/utils/get-complete-name";

import { PublicationNoItemText } from "../../../components/no-item-text";
import { PUBLICATION_MAP_ID } from "../../map/utils/map-id";
import { UsePublicationCommentReturn } from "../hooks/use-comment";

import { CommentThreadCondensed } from "./comment-components";

interface CommentThreadListProps {
  commentThreads: CommentRead[] | null;
  selectedCommentThreadId: string | undefined;
  searchValue: string;
  showResolved: boolean;
  setSelectedCommentThreadId: UsePublicationCommentReturn["setSelectedCommentThreadId"];
  removeCommentThread: UsePublicationCommentReturn["removeCommentThread"];
  editCommentMessage: UsePublicationCommentReturn["editCommentMessage"];
  resolveComment: UsePublicationCommentReturn["resolveComment"];
  toggleCommentReaction: UsePublicationCommentReturn["toggleCommentReaction"];
}

export function CommentThreadList({
  commentThreads,
  searchValue,
  selectedCommentThreadId,
  setSelectedCommentThreadId,
  removeCommentThread,
  editCommentMessage,
  resolveComment,
  toggleCommentReaction,
  showResolved,
}: CommentThreadListProps) {
  const { [PUBLICATION_MAP_ID]: map } = useMap();
  if (!commentThreads) return null;

  const handleClick = (location: Point, id: string) => {
    const [lng, lat] = location.coordinates;
    if (map) {
      const point = map.project([lng, lat]);
      // Offset by 100 pixels above center
      // to ensure space for the comment thread popup
      point.y -= 100;
      const offsetCenter = map.unproject(point);

      map.flyTo({
        center: offsetCenter,
        zoom: 10,
        duration: 2000,
      });
    }
    setSelectedCommentThreadId(id);
  };

  const getMatchesSearch = ({ body, creator, deleted }: MessagesInner) => {
    const searchLower = searchValue.toLowerCase();
    if (deleted) return false;
    return (
      (body || "").toLowerCase().includes(searchLower) ||
      getCompleteName(creator).toLowerCase().includes(searchLower)
    );
  };
  const noMatches =
    searchValue.trim() && !commentThreads.some(({ messages }) => messages.some(getMatchesSearch));

  return (
    <>
      <PublicationNoItemText show={!commentThreads.length}>
        <FormattedMessage
          id="comments.no_discussions"
          defaultMessage="There are no discussions yet click the map to place the first comment"
        />
      </PublicationNoItemText>

      <PublicationNoItemText show={!!noMatches}>
        <FormattedMessage
          id="comments.no_discussions_with_search"
          defaultMessage="There are no discussions matching the search"
        />
      </PublicationNoItemText>

      {commentThreads.map(({ messages, id, resolved, location }) => {
        const isHiddenByResolved = resolved && !showResolved;
        const isHiddenBySearch = searchValue.trim() && !messages.some(getMatchesSearch);

        return (
          <Collapse unmountOnExit in={!isHiddenByResolved && !isHiddenBySearch} key={id}>
            <CommentThreadCondensed
              threadId={id}
              messages={messages}
              resolved={resolved}
              isSelected={id === selectedCommentThreadId}
              onClick={() => handleClick(location, id)}
              removeCommentThread={removeCommentThread}
              editCommentMessage={(messageIndex, body) =>
                editCommentMessage({ commentId: id, messageIndex, body })
              }
              resolveComment={(type) => resolveComment({ commentId: id, type })}
              toggleCommentReaction={(messageIndex, emoji) =>
                toggleCommentReaction({ commentId: id, messageIndex, emoji })
              }
            />
          </Collapse>
        );
      })}
    </>
  );
}
